import React, { useState, useRef } from 'react';
import { Modal, Button, Input } from 'antd';
import { FileOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
  generateAiContent,
  generateAiContentWithFile
} from '../../../Apis/Jobs';
import { getAllCannedText } from '../../../Apis/Author';

const RoundedModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
  }
  .ant-modal-header {
    border-radius: 8px 8px 0 0;
  }
`;

const { TextArea } = Input;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  margin: -24px -24px 20px;
  padding: 0 24px;
`;

const Tab = styled.button`
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#47a4ea' : '#8c8c8c')};
  cursor: pointer;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? '#47a4ea' : 'transparent')};
  margin-bottom: -1px;
  background: none;
  font-family: inherit;
  transition: all 0.15s;
  &:hover {
    color: ${({ $active }) => ($active ? '#47a4ea' : '#595959')};
  }
`;

const Dropzone = styled.div`
  border: 1.5px dashed ${({ $dragOver }) => ($dragOver ? '#47a4ea' : '#d9d9d9')};
  border-radius: 8px;
  padding: 40px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  background: ${({ $dragOver }) => ($dragOver ? '#f0f8ff' : '#fafafa')};
  &:hover {
    border-color: #47a4ea;
    background: #f0f8ff;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  padding: 10px 14px;
  margin-top: 10px;
`;

const ExampleBtn = styled.button`
  display: block;
  width: 100%;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 8px 11px;
  font-size: 12px;
  color: #595959;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  margin-bottom: 5px;
  line-height: 1.5;
  transition: all 0.15s;
  &:hover {
    border-color: #47a4ea;
    color: #47a4ea;
    background: #f0f8ff;
  }
`;

const ErrorBox = styled.div`
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #ff4d4f;
  margin-top: 14px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8c8c8c;
  margin-bottom: 4px;
`;

const EXAMPLES = [
  'Psychiatric progress note for SNF (99309) — sections for chief complaint, interval history (concise), review of systems (brief), mental status exam, risk assessment, medical/psychiatric updates, current medications, collateral reviewed, assessment and plan',
  'Initial H&P for a new SNF admission — presenting illness, past medical history, allergies, current medications, physical exam, review of systems, functional status, diagnoses, and plan of care'
];

const SYSTEM_PROMPT_TEMPLATE = `IMPORTANT: Analyze the string in the user content, with the instruction in the content you have to generate a response in this format, {
  "templateName": "string",
  "templateDescription": "string",
  "sectionHeadings": [
    {
      "type": "HEADING",
      "customUIHeading": "string",
      "systemHeading": "None",
      "description": "string",
      "defaultValue": "string",
      "appendToDefault": false,
      "submitToEmr": true,
      "suppressOnEmpty": false,
      "useAI": false,
      "aiPrompt": null,
      "required": false,
      "fieldType": "TEXT",
      "options": []
    },
    {
      "type": "SECTION_GROUP",
      "customUIHeading": "string",
      "push_title_to_note": false,
      "children": [
        {
          "type": "HEADING",
          "customUIHeading": "string",
          "systemHeading": "None",
          "description": "string",
          "defaultValue": "string",
          "appendToDefault": false,
          "submitToEmr": true,
          "suppressOnEmpty": false,
          "useAI": false,
          "aiPrompt": null,
          "required": false,
          "fieldType": "TEXT",
          "options": []
        }
      ]
    }
  ]
}
your response should be strictly in this format, do not deviate from this format in howsoever condition, if there is not enough data to give response in the said format, just give empty string as response but do not deviate from this format. The keys in the json i have shared is templateName — The name that identifies this dictation template in the list. What clinicians see when picking a template to use.

templateDescription — A short summary of what the template is for. Helps users understand the template's purpose before selecting it.

---

Template Structure

A template is made up of items in the sectionHeadings array. Each item is either a standalone HEADING or a SECTION_GROUP that contains multiple headings. Use your judgment to decide which sections should be grouped together and which should stand alone.

HEADING — A standalone section the dictator speaks into or interacts with directly.

SECTION_GROUP — A named container that groups related headings together. Use this when multiple headings belong to the same clinical area (e.g. grouping "Blood Pressure", "Heart Rate", "Temperature" under a "Vital Signs" group). A section group has:
  customUIHeading — The group title displayed on screen.
  push_title_to_note — Set to true if the group title itself should appear as a label in the generated note. Set to false if the group is just for visual organization and the title should not appear in the output.
  children — An array of HEADING objects that belong to this group.

---

Section Heading Fields

customUIHeading — The label the dictator sees on screen for this section.

systemHeading — Always set this to None.

description — A tooltip that appears when the dictator hovers over the section heading. Used to give guidance or instructions for what to say in that section.

defaultValue — Pre-filled text that appears in the section before the dictator says anything. The defaultValue can be key to phrases available for the user, in this case the user has these key to phrases on his disposal: {k2p}. When populating defaultValue, use the key and not the phrase.

appendToDefault — Controls what happens when a dictator speaks into a section that has a defaultValue. If OFF, their dictation replaces the default. If ON, their dictation is added after the default text, keeping both.

submitToEmr — Whether this section's content gets sent to the EMR at all. If turned off, the section is visible to the dictator but its content is never submitted — useful for internal notes, intake questions, or structured fields that guide the dictation but are not part of the final note.

suppressOnEmpty — If the dictator leaves a section blank, this controls whether the section heading still appears in the final document. When ON, an empty section is completely hidden from the output rather than showing an empty heading.

useAI — Marks this section to be auto-filled by AI based on the rest of the dictation, instead of requiring the dictator to speak it explicitly. Only one section per template can have this on.

aiPrompt — The instruction given to the AI explaining what to generate for this section. Only relevant when useAI is on — e.g. "Summarize the patient's visit into a concise assessment and plan."

required — Whether the dictator must provide content for this section before they can submit the job. If on, leaving it blank blocks submission.

fieldType — The input type for this section. Use "TEXT" for normal dictation sections. Use "CHECKBOX" for sections where the dictator picks one or more items from a fixed list (e.g. symptoms present, systems reviewed). Use "DROPDOWN" for sections where the dictator picks a single value from a fixed list (e.g. visit type, level of care). IMPORTANT: fieldType must only be "CHECKBOX" or "DROPDOWN" when submitToEmr is false — these are structured input fields that guide the dictation and are not submitted to the EMR.

options — An array of string values listing the choices available when fieldType is "CHECKBOX" or "DROPDOWN". Leave as an empty array [] when fieldType is "TEXT".`;

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const AIGenerateModal = ({ visible, onClose, onSuccess, k2ps = [] }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [description, setDescription] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const reset = () => {
    setActiveTab('upload');
    setFile(null);
    setDragOver(false);
    setDescription('');
    setTemplateName('');
    setGenerating(false);
    setError('');
  };

  const handleClose = () => {
    if (generating) return;
    reset();
    onClose();
  };

  const handleFileSelect = f => {
    if (!f) return;
    setFile(f);
    setError('');
    if (!templateName.trim()) {
      setTemplateName(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '));
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isGenerateDisabled = () => {
    if (!templateName.trim()) return true;
    if (activeTab === 'upload') return !file;
    return !description.trim();
  };

  const handleGenerate = async () => {
    setError('');
    setGenerating(true);

    try {
      const resolvedK2ps = k2ps.length
        ? k2ps
        : (await getAllCannedText('allMine'))?.data?.result || [];
      const k2pList = resolvedK2ps.filter(k => !k.restrictedToTemplates);
      const k2pFormatted = k2pList.length
        ? k2pList
            .map(
              k =>
                `key: ${k.key} | phrase: ${(k.phrase || '')
                  .replace(/<[^>]*>/g, '')
                  .trim()}`
            )
            .join('\n')
        : 'none';
      const systemContent = SYSTEM_PROMPT_TEMPLATE.replace(
        '{k2p}',
        k2pFormatted
      );

      if (activeTab === 'describe') {
        const messages = [
          { role: 'system', content: systemContent },
          {
            role: 'user',
            content: `${description.trim()}. The template name is: ${templateName.trim()}`
          }
        ];
        const response = await generateAiContent(messages);
        const rawJson = response?.data?.result?.[0];
        if (!rawJson)
          throw new Error('No template data returned. Please try again.');
        const generated = JSON.parse(rawJson);
        reset();
        onSuccess(generated);
      } else {
        const messages = [
          { role: 'system', content: systemContent },
          {
            role: 'user',
            content: `Analyze the file and give response according to the system instruction ${description.trim()}. The template name is: ${templateName.trim()}`
          }
        ];
        const response = await generateAiContentWithFile(messages, file);
        const rawJson = response?.data?.result?.[0];
        if (!rawJson)
          throw new Error('No template data returned. Please try again.');
        const generated = JSON.parse(rawJson);
        reset();
        onSuccess(generated);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Something went wrong. Please try again.'
      );
      setGenerating(false);
    }
  };

  const isUpload = activeTab === 'upload';

  return (
    <RoundedModal
      title={<span>✦ Generate template with AI</span>}
      visible={visible}
      onCancel={handleClose}
      width={560}
      style={{ top: 150 }}
      destroyOnClose
      maskClosable={!generating}
      closable={!generating}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button shape='round' onClick={handleClose} disabled={generating}>
            Cancel
          </Button>
          <Button
            shape='round'
            type='primary'
            disabled={isGenerateDisabled() || generating}
            loading={generating}
            onClick={handleGenerate}
            style={{
              background: '#47a4ea',
              borderColor: '#47a4ea',
              color: '#fff'
            }}
          >
            ✦ Generate template
          </Button>
        </div>
      }
    >
      <TabRow>
        <Tab $active={isUpload} onClick={() => setActiveTab('upload')}>
          📄 Upload document
        </Tab>
        <Tab $active={!isUpload} onClick={() => setActiveTab('describe')}>
          ✏️ Describe
        </Tab>
      </TabRow>

      {isUpload ? (
        <div>
          <Dropzone
            $dragOver={dragOver}
            onDragOver={e => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <HiddenFileInput
              ref={fileInputRef}
              type='file'
              accept='.pdf,.doc,.docx,.txt,.csv,.json,.jpg,.jpeg,.png,.gif,.webp'
              onChange={e => handleFileSelect(e.target.files[0])}
            />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📄</div>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
              Drop your document here or click to upload
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              PDF, Word, image, or text file
            </div>
          </Dropzone>
          {file && (
            <FileChip>
              <FileOutlined style={{ fontSize: 16, color: '#52c41a' }} />
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  flex: 1,
                  color: '#389e0d'
                }}
              >
                {file.name}
              </span>
              <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                {formatFileSize(file.size)}
              </span>
              <Button
                type='text'
                size='small'
                icon={<CloseOutlined />}
                onClick={clearFile}
                style={{ color: '#8c8c8c' }}
              />
            </FileChip>
          )}
        </div>
      ) : (
        <div>
          <FieldLabel>Prompt</FieldLabel>
          <div style={{ position: 'relative' }}>
            <TextArea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder='Describe the note template — sections needed, specialty, note type, any specific requirements…'
              autoSize={{ minRows: 5, maxRows: 10 }}
              style={{ paddingBottom: 24, borderRadius: 6 }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 8,
                right: 10,
                fontSize: 10,
                color: '#bfbfbf',
                pointerEvents: 'none'
              }}
            >
              {description.length} chars
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 11,
                color: '#8c8c8c',
                marginBottom: 6,
                fontWeight: 500
              }}
            >
              Examples — click to load, then edit:
            </div>
            {EXAMPLES.map((ex, i) => (
              <ExampleBtn key={i} onClick={() => setDescription(ex)}>
                {ex}
              </ExampleBtn>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <FieldLabel>
          Template name <span style={{ color: '#ff4d4f' }}>*</span>
        </FieldLabel>
        <Input
          value={templateName}
          onChange={e => setTemplateName(e.target.value)}
          placeholder='e.g. Dr. Chen – SNF Recertification'
          style={{ borderRadius: 6 }}
        />
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
    </RoundedModal>
  );
};

export default AIGenerateModal;
